import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  EditCellV8,
  CellCenterV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  EyeColumnV8,
  toastFailure,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  firstDateUserColumn,
  isActiveColumn,
  lastDateUserColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
  dateExpiryColumn,
} from 'shared-pages/common/grid-helpfunc';
import {
  collectorsWhere,
  useGetCollectorAsOptionsQuery,
} from 'shared-pages/components';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { isRebattery } from 'shared-pages/common/constants';
import { useHandleError } from 'shared-pages/services';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const { handleGraphqlError } = useHandleError('driver');
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    isGridInForm,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateDriver] = useMutation(gqlUpdate);
  const [addNewDriver] = useMutation(gqlAdd);
  const [deleteDriver] = useMutation(gqlDelete);

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('Driver'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );
  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellCenterV8,
        size: 5,
      },
      {
        id: 'name',
        header: t('Ονοματεπώνυμο Οδηγού'),
        columnName: t('Ονοματεπώνυμο Οδηγού'),
        accessorKey: 'name',
        orderByField: 'name',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      ...(!isGridInForm && appStore.isPlatformOwner
        ? [
            {
              id: 'collectorId',
              header: t('Συλλέκτης'),
              columnName: t('Συλλέκτης'),
              accessorKey: 'collectorId',
              orderByField: 'collector.name',
              cell: TableCellV8,
              size: 50,
              meta: {
                type: 'select',
                hook: useGetCollectorAsOptionsQuery,
                where: collectorsWhere,
                idKey: 'collectorId',
                idName: 'collector.name',
              },
            },
          ]
        : []),
      ...(!isRebattery
        ? [
            {
              id: 'phone',
              header: t('Τηλέφωνο'),
              columnName: t('Τηλέφωνο'),
              accessorKey: 'phone',
              orderByField: 'phone',
              cell: TableCellV8,
              size: 20,
              meta: { type: 'text' },
            },
          ]
        : []),

      {
        id: 'adrNumber',
        header: t('Αριθμός ADR'),
        columnName: t('Αριθμός ADR'),
        accessorKey: 'adrNumber',
        orderByField: 'adrNumber',
        cell: TableCellV8,
        size: 20,
        meta: { type: 'text' },
      },

      {
        id: 'adrExpDate',
        header: t('Λήξη ADR'),
        columnName: t('Λήξη ADR'),
        accessorKey: 'adrExpDate',
        orderByField: 'adrExpDate',
        ...dateExpiryColumn(t),
      },
      /* {
        id: 'vehicle.plateNumber',
        header: t('Όχημα'),
        columnName: t('vehicle.plateNumber'),
        accessorKey: 'vehicle.plateNumber',
        orderByField: 'vehicle.plateNumber',
        cell: TableCellV8,
        size: 20,
        meta: { type: 'text' },
      },
      {
        id: 'user.userName',
        header: t('Χρήστης'),
        columnName: t('Χρήστης'),
        accessorKey: 'user.userName',
        orderByField: 'user.userName',
        cell: TableCellV8,
        size: 20,
        meta: { type: 'text' },
      }, */
      isActiveColumn(t, i18n),
      firstDateUserColumn(t),
      lastDateUserColumn(t),
      ...(!isGridInForm && appStore.isPlatformOwner
        ? [
            {
              id: 'edit',
              header: t('Ενέργειες'),
              columnName: t('Ενέργειες'),
              accessorKey: 'edit',
              orderByField: 'lastUser',
              size: 120,
              cell: ({ row, table }) => {
                return true ? EditCellV8({ row, table }) : <>-</>;
              },
            },
          ]
        : []),
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          style={{ overflow: 'visible' }}
          ref={tableRef}
          loading={loading}
          id="drivers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
          onNewRow={() => {
            return {
              id: t('*'),
              isActive: true,
              collectorId: appStore.isCollector ? appStore.associatedId : undefined,
            };
          }}
          onBeforeSaveRow={async (state, row, changes) => {
            console.log('$$$ onBeforeSaveRow', state, row, changes);
            if (row?.isNotActive !== undefined && row.isNotActive === true) return true;
            // Validate
            if (!row.collectorId && !changes.collectorId) {
              toastFailure(t('Πρέπει να δηλώσετε Συλλέκτη'));
              return false;
            }
            return true;
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            //
            // i.e. Save Record
            //
            let id = row.id;
            console.log('$$$ Drivers - OnSaveRow row', row, changes);
            const values =
              tableStore.state === TableDataState.insert ? { ...row } : { id: id, ...changes }; // Το changes δεν έχει το collectorId
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
            // [α] Insert New Record
            if (tableStore.state === TableDataState.insert) {
              mutValues.id = null;
              const res = await addNewDriver({ variables: { data: mutValues } }).catch((reason) => {
                console.error('$$$ grid.UpdateRow insert error reason', reason);
              });
              if (!res) return false;
              toastSuccess('Επιτυχής Προσθήκη Οδηγού');
              // some error returned
              id = res.data.addDriver.id;
            }
            // ή [β] Update Existing Record
            else if (tableStore.state === TableDataState.edit) {
              const res = await updateDriver({ variables: { data: mutValues } }).catch((reason) => {
                console.error('$$$ grid.UpdateRow insert error reason', reason);
              });
              if (!res) return false; // some error returned
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε τον Οδηγό <strong>${row.name}</strong>;</div>`,
            });
            if (!ans) {
              return;
            }
            const res = await deleteDriver({ variables: { id: row.id } }).catch((reason) => {
              toastFailure(<div>Αδυναμία διαγραφής του Οδηγού. Μήπως χρησιμοποιείται ήδη;<br/> {reason?.message || String(reason)}</div>);
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) {
              return false; // some error returned
            }
            refetch();
            toastSuccess('Διαγράφηκε ο Οδηγός');
            return true;
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
